import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getRequest } from 'api/apiClient';
import { AppDispatch, RootState } from 'store/store';
import { formatFullName, statusKeyMap } from 'utils/utils';
import { getVisitDateTime, getStatusNameById, getStatusColor, profileIcon, getVisitorDateTime, isVisitorRedFlagged } from 'utils/visitorsHelper';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import isBetween from 'dayjs/plugin/isBetween';
import { DATE_FORMAT, DATE_TIME_AM_PM_FORMAT } from 'constants/dates';
import { MenuProps } from 'antd';
import { OpDropdown } from 'components/customAntd/DLS/OpDropdown/OpDropdown';
import { OpTable, IOpTableProps } from 'components/customAntd/DLS/OpTable/OpTable';
import { OpTableRawColumnType } from 'components/customAntd/DLS/OpTableCore/OpTableCore';
import { VideoCameraOutlined } from '@ant-design/icons';
import { TABLE_HEIGHT } from 'constants/ui';
import warningIcon from 'images/icons/warning.svg';
import { Visit, Visitor, VisitStatus } from 'types/visitTypes';
import { setSelectedVisit, setSelectedVisitor } from 'store/slices/visitsSlice';
import { botLoginAltaVideo, checkThumbnailPresence } from 'store/slices/altaVideoSlice';
import { fetchRedFlags } from 'store/slices/redFlagSlice';
import STATUS from 'constants/status';
import ActivityLogVideoModal from './drawer/profile/tabs/ActivityLogVideoModal';
import { useTranslation } from 'react-i18next';

dayjs.extend(isBetween);
dayjs.extend(utc);
dayjs.extend(timezone);

interface ProcessedVisitor extends Visitor {
    fullName: string;
    statusName: string;
}

interface ProcessedVisit extends Visit {
    visit: Visit;
    dateTime: string | null;
    fullName: string;
    statusName: string;
    visitors: ProcessedVisitor[];
    children: any;
}

interface VisitorsTableProps {
    loading: boolean;
    openVisitorsDrawer: () => void;
    openProfileDrawer: () => void;
}

const VisitorsTable: React.FC<VisitorsTableProps> = ({ loading, openVisitorsDrawer, openProfileDrawer }) => {
    const dispatch: AppDispatch = useDispatch();

    // Redux state
    const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
    const redFlags = useSelector((state: RootState) => state.redFlags.redFlags);
    const { visits, visitsStartDate, visitsEndDate, selectedVisit } = useSelector((state: RootState) => state.visits);
    const { videoDevices, devicesByWorkflow, thumbnailPresence } = useSelector((state: RootState) => state.altaVideo);

    // Local state
    const [visitStatus, setVisitStatus] = useState<VisitStatus[]>([]);
    const [statusFilterOptions, setStatusFilterOptions] = useState<{ label: string; value: string }[]>([]);
    const [visitorTypeFilterOptions, setVisitorTypeFilterOptions] = useState<{ label: string; value: number }[]>([]);
    const [defaultStatusFilter, setDefaultStatusFilter] = useState<string[]>([]);
    const [defaultvisitorTypeFilter, setDefaultvisitorTypeFilter] = useState<string[]>([]);
    const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
    const [modalTargetType, setModalTargetType] = useState<'SIGNIN' | 'SIGNOUT'>();

    const { t } = useTranslation();

    // Current siteId & devicesByWorkflowForSite 
    const visitorSiteId = selectedVisit?.site?.id ?? null;
    const devicesByWorkflowForSite = visitorSiteId ? devicesByWorkflow[visitorSiteId] : null;

    // Fetch visit statuses
    useEffect(() => {
        const fetchVisitStatuses = async () => {
            try {
                const visitorStatus = await getRequest(`/orgs/${orgId}/visitorStatus`);
                setVisitStatus(visitorStatus.data);
            } catch (e) {
                console.error('Failed to fetch visit statuses', e);
            }
        };

        if (orgId) {
            fetchVisitStatuses();
        }
    }, [orgId]);

    // Fetch visitor types
    useEffect(() => {
        const fetchVisitorTypes = async () => {
            try {
                const visitorTypes = await getRequest(`/orgs/${orgId}/visitorTypes`);
                const options = visitorTypes.data.map((type: any) => ({
                    label: t(type.languageCode ?? '', type.name),
                    value: type.id,
                }));
                setVisitorTypeFilterOptions(options);
                setDefaultvisitorTypeFilter(options.map((option: { value: any; }) => option.value));
            } catch (e) {
                console.error('Failed to fetch visit types', e);
            }
        };

        if (orgId) {
            fetchVisitorTypes();
        }
    }, [orgId, t]);

    // Fetch red flags
    useEffect(() => {
        const fetchData = async () => {
            try {
                if (redFlags.length === 0) {
                    await dispatch(fetchRedFlags({ orgId }));
                }
            } catch (error) {
                console.log("Failed to fetch data.");
            }
        };
        fetchData();
    }, [dispatch, t, orgId, redFlags.length]);

    // Status filter settings
    useEffect(() => {
        if (visitStatus.length > 0) {
            const options = [
                ...visitStatus.map(status => ({
                    label: t(status.languageCode ?? '', status.name ?? ''),
                    value: status.name,
                })),
                {
                    label: t('vst_data_noshow', 'No Show'),
                    value: 'No Show',
                },
            ];

            setStatusFilterOptions(options);
            setDefaultStatusFilter(options.map(option => option.value));
        }
    }, [visitStatus, t]);

    // ---------------- Preprocess the visits to add fullName and dateTime fields ----------------

    const processedVisits = useMemo(() => {
        return visits
            .filter(visit => {
                let dateTime = null;

                if (visit.visitStatus.id === STATUS.PENDING.id) {
                    dateTime = visit.scheduleStart;
                } else if (visit.visitStatus.id === STATUS.SIGNED_IN.id) {
                    dateTime = visit.signIn;
                } else if (visit.visitStatus.id === STATUS.SIGNED_OUT.id) {
                    dateTime = visit.signOut;
                } else if (visit.visitStatus.id === STATUS.DENIED_ENTRY.id) {
                    dateTime = visit.signIn;
                }

                return dateTime && dayjs(dateTime).isBetween(dayjs(visitsStartDate), dayjs(visitsEndDate), null, '[]');
            }).map(visit => ({
                ...visit,
                visit: visit,
                dateTime: getVisitDateTime(visit, getStatusNameById(visit.visitStatus.id, visit.scheduleStart)),
                fullName: formatFullName(visit.visitors[0]?.firstName, visit.visitors[0]?.middleName, visit.visitors[0]?.lastName)
                    + (visit.visitors.length > 1 ? ` (+${visit.visitors.length - 1})` : ''),
                email: visit.visitors?.[0]?.email ?? null,
                statusName: getStatusNameById(visit.visitStatus.id, visit.scheduleStart),
                locationName: visit.site?.name ?? '',
                hostName: visit.host ? formatFullName(visit.host.firstName, null, visit.host.lastName) : t('visitors.table.host.unassigned'),
                phoneNumber: visit.visitors?.[0]?.mobilePhone ?? null,
                company: visit.company ?? null,
                visitorType: visit.visitorType ?? null,
                visitorTypeName: visit.visitorType.name ?? '',

                visitors: visit.visitors.map(visitor => ({
                    ...visitor,
                    fullName: formatFullName(visitor.firstName, visitor.middleName, visitor.lastName),
                    statusName: visitor.status ? getStatusNameById(visitor.status, visit.scheduleStart) : undefined
                })),
                host: {
                    id: visit.host?.userId?.toString() ?? '-1',
                    name: visit.host
                        ? formatFullName(visit.host.firstName, null, visit.host.lastName)
                        : t('visitors.table.host.unassigned'),
                },
                children: visit.visitors.length > 1
                    ? visit.visitors.map(visitor => ({
                        visit: visit,
                        visitor: visitor,
                        fullName: formatFullName(visitor.firstName, visitor.middleName, visitor.lastName),
                        email: visitor.email ?? null,
                        statusName: getStatusNameById(visitor.status, visit.scheduleStart),
                        dateTime: getVisitorDateTime(visitor, getStatusNameById(visitor.status, visit.scheduleStart), visit.scheduleStart, DATE_TIME_AM_PM_FORMAT),
                        locationName: visit.site?.name ?? '',
                        hostName: visit.host ? formatFullName(visit.host.firstName, null, visit.host.lastName) : t('visitors.table.host.unassigned'),
                        phoneNumber: visitor.mobilePhone ?? null,
                        company: visit.company ?? null,
                        visitorType: visit.visitorType ?? null,

                    })) : undefined
            }));
    }, [visits, visitsStartDate, visitsEndDate, t]);

    // Check if any visitor in the visit is red-flagged
    const isRedFlagged = (processedVisit: ProcessedVisit) => {
        return processedVisit.visitors.some(visitor =>
            isVisitorRedFlagged(visitor, redFlags)
        );
    };

    // Get site timezone with site id
    const getSiteTimezone = (siteId?: number | null) => {
        if (!siteId) return Intl.DateTimeFormat().resolvedOptions().timeZone;
        return devicesByWorkflow[siteId]?.site?.timezone // Use system location timezone if timezone is null
    };

    const uniqueHostOptions = Array.from(
        new Map(
            processedVisits.map(v => [v.host.id, v.host.name])
        ).entries()
    )
        .filter(([id, name]) => id !== null && name !== null)
        .map(([id, name]) => ({
            label: id === '-1' ? t('visitors.table.host.unassigned') : name!,
            value: id!
        }))
        .sort((a, b) => (a.value === '-1' ? 1 : b.value === '-1' ? -1 : 0));


    const uniqueLocationOptions = Array.from(
        new Map(
            processedVisits.map(v => [v.site.id, v.site.name])
        ).entries()
    )
        .filter(([id, name]) => id !== null && name !== null)
        .map(([id, name]) => ({ label: name!, value: id! }));


    // Check thumbnail presence
    useEffect(() => {
        if (!devicesByWorkflow || processedVisits.length === 0 || videoDevices.length === 0) return;

        const checkThumbnails = async () => {

            try {
                await dispatch(botLoginAltaVideo());

                const allPromises: Promise<any>[] = [];

                processedVisits.forEach((visit) => {
                    const siteId = visit?.site?.id;
                    if (!siteId || !devicesByWorkflow[siteId]) return;

                    const workflow = devicesByWorkflow[siteId];
                    const combinedInIdDevices = [...(workflow.signInDeviceId ?? []), ...(workflow.kioskInDeviceId ?? [])];
                    const combinedOutDeviceIds = [...(workflow.signOutDeviceId ?? []), ...(workflow.kioskOutDeviceId ?? [])];

                    visit.visitors.forEach((visitor) => {
                        const presence = thumbnailPresence[visit.id]?.[visitor.id];

                        if (visitor.signIn && presence?.SIGNIN === undefined) {
                            const baseUtc = dayjs.tz(visitor.signIn, getSiteTimezone(siteId)).utc().valueOf();
                            combinedInIdDevices.forEach((deviceId) => {
                                allPromises.push(dispatch(checkThumbnailPresence({
                                    visitId: visit.id.toString(),
                                    visitorId: visitor.id.toString(),
                                    type: 'SIGNIN',
                                    time: baseUtc,
                                    deviceId,
                                })));
                            });
                        }

                        if (visitor.statusName === "Signed Out" && visitor.signOut && presence?.SIGNOUT === undefined) {
                            const baseUtc = dayjs.tz(visitor.signOut, getSiteTimezone(siteId)).utc().valueOf();
                            combinedOutDeviceIds.forEach((deviceId) => {
                                allPromises.push(dispatch(checkThumbnailPresence({
                                    visitId: visit.id.toString(),
                                    visitorId: visitor.id.toString(),
                                    type: 'SIGNOUT',
                                    time: baseUtc,
                                    deviceId,
                                })));
                            });
                        }
                    });
                });

                await Promise.all(allPromises);

            } catch (error) {
                console.error('Error checking thumbnails presence', error);
            }
        };

        checkThumbnails();

        // eslint-disable-next-line
    }, [processedVisits, videoDevices, devicesByWorkflow, dispatch]);

    // ---------------- Render ----------------

    const columns: OpTableRawColumnType[] = [
        {
            dataIndex: 'fullName',
            label: t('visitors.table.fullName'),
            filter: { type: 'input' },
            sorter: (a, b) => a.fullName.localeCompare(b.fullName),
            onFilter: (value, record) => {
                const searchValue = value.toString().toLowerCase();
                const mainNameMatch = record.fullName.toLowerCase().includes(searchValue);
                const visitorNameMatch = record.visitors.some((visitor: ProcessedVisitor) =>
                    visitor.fullName?.toLowerCase().includes(searchValue)
                );
                return mainNameMatch || visitorNameMatch;
            },
            render: (value, record) => {
                // Check if this is a parent visit record or a child visitor record
                const isParentRow = !!record.visitors; // If it has the "visitors" property, it's a parent row

                if (isParentRow) {
                    // This is a visit record (parent row)
                    const visitRecord = record as ProcessedVisit;
                    // thumbnail presense for a visitor (single visitor in a visit)
                    const visitorId = visitRecord.visitors?.[0]?.id;
                    const presence = thumbnailPresence?.[visitRecord.id]?.[visitorId];
                    const hasAnyThumbnail = visitRecord.visitors?.length === 1 && (presence?.SIGNIN === true || presence?.SIGNOUT === true);
                    // Handle click event for menu
                    const handleMenuClick: MenuProps['onClick'] = (value) => {
                        dispatch(setSelectedVisit(visitRecord.visit));
                        openVisitorsDrawer();
                        const selectedVisitor = visitRecord.visitors.find((visitor: ProcessedVisitor) => visitor.id === Number(value.key));
                        if (selectedVisitor) {
                            dispatch(setSelectedVisitor(selectedVisitor));
                        }
                        openProfileDrawer();
                    };

                    // Menu items for visitors
                    const menuItems: MenuProps['items'] = visitRecord.visitors.map((visitor: Visitor) => {
                        const visitorColorCode = getStatusColor(getStatusNameById(visitor.status!, visitRecord.scheduleStart));
                        return {
                            key: visitor.id?.toString() || '',
                            label: (
                                <span style={{ display: 'flex', alignItems: 'center' }}>
                                    {profileIcon({ visitor, size: 28, color: visitorColorCode })}
                                    <span>{formatFullName(visitor.firstName, visitor.middleName, visitor.lastName)}</span>
                                </span>
                            ),
                        };
                    });

                    const visitColorCode = getStatusColor(visitRecord.statusName);
                    const profileIconComponent = (
                        <span style={{ display: 'flex', alignItems: 'center' }}>
                            {profileIcon({ visitor: visitRecord.visitors[0], size: 28, color: visitColorCode })}
                            <span style={{ wordBreak: 'break-word' }}>
                                {value}
                            </span>
                            {isRedFlagged(visitRecord) && (
                                <img src={warningIcon} alt="Warning" style={{ marginLeft: '8px', width: '16px', height: '16px' }} />
                            )}
                        </span>
                    );

                    // If visit has more than one visitor, show a dropdown on hover
                    return (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>

                            {visitRecord.visitors.length > 1 ? (
                                <OpDropdown
                                    menu={{ items: menuItems, onClick: handleMenuClick }}
                                    trigger={['hover']}
                                >
                                    <span style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                        {profileIconComponent}
                                    </span>
                                </OpDropdown>
                            ) : (
                                profileIconComponent
                            )}

                            {hasAnyThumbnail && (
                                <VideoCameraOutlined
                                    onClick={(e) => {
                                        e.stopPropagation();

                                        let type: 'SIGNIN' | 'SIGNOUT' = "SIGNIN";
                                        if (visitRecord.statusName === "Signed Out" && presence?.SIGNOUT) {
                                            type = "SIGNOUT";
                                        } else if (presence?.SIGNIN) {
                                            type = "SIGNIN";
                                        } else {
                                            type = "SIGNOUT";
                                        }

                                        dispatch(setSelectedVisit(visitRecord.visit));
                                        if (visitRecord.visitors?.[0]) {
                                            dispatch(setSelectedVisitor(visitRecord.visitors[0]));
                                        }
                                        setModalTargetType(type);
                                        setIsVideoModalOpen(true);
                                    }}
                                    style={{ cursor: 'pointer', marginLeft: 12, fontSize: 16 }}
                                />
                            )}
                        </div>
                    );
                } else {
                    // This is a visitor record (child row)
                    const visitorRecord = record as any;
                    // Thumbnail presence for a current visitor (while rendering)
                    const presence = thumbnailPresence?.[visitorRecord.visit.id]?.[visitorRecord.visitor.id];
                    const hasThumbnail = presence?.SIGNIN === true || presence?.SIGNOUT === true;
                    return (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>

                            <span style={{ display: 'flex', alignItems: 'center' }}>
                                {profileIcon({ fullName: visitorRecord.fullName, size: 28, color: getStatusColor(visitorRecord.statusName) })}
                                <span style={{ wordBreak: 'break-word' }}>
                                    {value}
                                </span>
                                {isVisitorRedFlagged(visitorRecord, redFlags) && (
                                    <img src={warningIcon} alt="Warning" style={{ marginLeft: '8px', width: '16px', height: '16px' }} />
                                )}
                            </span>

                            {hasThumbnail && (
                                <VideoCameraOutlined
                                    onClick={(e) => {
                                        e.stopPropagation();

                                        let type: 'SIGNIN' | 'SIGNOUT' = "SIGNIN";
                                        if (visitorRecord.statusName === "Signed Out" && presence?.SIGNOUT) {
                                            type = "SIGNOUT";
                                        } else if (presence?.SIGNIN) {
                                            type = "SIGNIN";
                                        } else {
                                            type = "SIGNOUT";
                                        }

                                        if (visitorRecord.visit) {
                                            dispatch(setSelectedVisit(visitorRecord.visit));
                                        }
                                        if (visitorRecord.visitor) {
                                            dispatch(setSelectedVisitor(visitorRecord.visitor));
                                        }
                                        setModalTargetType(type);
                                        setIsVideoModalOpen(true);
                                    }}
                                    style={{ cursor: 'pointer', marginLeft: 12, fontSize: 16 }}
                                />
                            )}
                        </div>
                    );
                }
            }
        },
        {
            dataIndex: 'email',
            label: t('visitors.table.email'),
            filter: { type: 'input' },
            sorter: (a, b) => (a.email || '').localeCompare(b.email || ''),
            onFilter: (value, record) => (record.email || '').toLowerCase().includes(String(value).toLowerCase()),
            render: (value) => {
                return value;
            },
            hidden: true
        },
        {
            dataIndex: 'statusName',
            label: t('visitors.table.status'),
            filter: {
                type: 'multiSelect',
                options: statusFilterOptions,
            },
            defaultFilteredValue: defaultStatusFilter,
            sorter: (a, b) => a.statusName.localeCompare(b.statusName),
            onFilter: (value, record) => {
                const mainStatusMatch = record.statusName === value;
                const visitorStatusMatch = record.visitors.some((visitor: ProcessedVisitor) =>
                    visitor.statusName === value
                );
                return mainStatusMatch || visitorStatusMatch;
            },
            render: (value) => {
                const color = getStatusColor(value);
                const translated = t(statusKeyMap[value] || value);
                return (
                    <div style={{ backgroundColor: color, padding: '5px 10px', borderRadius: '4px', color: 'white' }}>
                        {translated}
                    </div>
                );
            }
        },
        {
            dataIndex: 'dateTime',
            label: t('visitors.table.dateTime'),
            sorter: (a, b) => dayjs(b.dateTime).diff(a.dateTime)
        },
        {
            dataIndex: 'locationName',
            label: t('visitors.table.location'),
            filter: {
                type: 'multiSelect',
                options: uniqueLocationOptions,
            },
            sorter: (a, b) => a.locaitonName.localeCompare(b.locaitonName),
            onFilter: (value, record) => {
                return String(record.site?.id) === String(value);
            },
            hidden: true
        },
        {
            dataIndex: 'hostName',
            label: t('visitors.table.host.label'),
            filter: {
                type: 'multiSelect',
                options: uniqueHostOptions,
            },
            sorter: (a, b) => a.hostName.localeCompare(b.hostName),
            onFilter: (value, record) => {
                return String(record.host.id) === String(value)
            },
            hidden: true,
        },
        {
            dataIndex: 'phoneNumber',
            label: t('visitors.table.phoneNumber'),
            sorter: (a, b) => (a.phoneNumber || '').localeCompare(b.phoneNumber || ''),
            filter: { type: 'input' },
            render: (value) => {
                return value;
            },
            hidden: true,
        },
        {
            dataIndex: 'company',
            label: t('visitors.table.company'),
            filter: { type: 'input' },
            sorter: (a, b) => (a.company || '').localeCompare(b.company || ''),
            render: (value) => {
                return value;
            },
            hidden: true,
        },
        {
            dataIndex: 'visitorTypeName',
            label: t('visitors.table.visitorType'),
            filter: {
                type: 'multiSelect',
                options: visitorTypeFilterOptions,
            },
            defaultFilteredValue: defaultvisitorTypeFilter,
            sorter: (a, b) => (a.visitorTypeName || '').localeCompare(b.visitorTypeName || ''),
            onFilter: (value, record) => {
                return String(record.visitorType?.id) === String(value);
            },
            render: (value, record) => {
                return t(record.visitorType?.languageCode ?? '');
            },
            hidden: true,
        },
    ];

    const opTableProps: IOpTableProps = {
        dataSource: processedVisits,
        label: `${dayjs(visitsStartDate).format(DATE_FORMAT)} - ${dayjs(visitsEndDate).format(DATE_FORMAT)}`,
        columns: columns,
        rowActions: {
            onEditClick: (visit: any) => {
                const isParentRow = !!visit.visitors;
                if (isParentRow) {
                    dispatch(setSelectedVisit(visit.visit));
                    openVisitorsDrawer();
                } else {
                    dispatch(setSelectedVisit(visit.visit));
                    dispatch(setSelectedVisitor(visit.visitor));
                    openVisitorsDrawer();
                    openProfileDrawer();
                }
            },
        },
        expandable: {
            rowExpandable: (record) => record.visitors.length > 1,  // Show "+" if there are multiple visitors
        },
        height: TABLE_HEIGHT,
        allowGlobalSearch: true,
        loading: loading,
    };

    const mergedSignInDeviceIds = useMemo(() => {
        if (!devicesByWorkflowForSite) return [];
        return Array.from(new Set([
            ...(devicesByWorkflowForSite.signInDeviceId ?? []),
            ...(devicesByWorkflowForSite.kioskInDeviceId ?? [])
        ]));
    }, [devicesByWorkflowForSite]);

    const mergedSignOutDeviceIds = useMemo(() => {
        if (!devicesByWorkflowForSite) return [];
        return Array.from(new Set([
            ...(devicesByWorkflowForSite.signOutDeviceId ?? []),
            ...(devicesByWorkflowForSite.kioskOutDeviceId ?? [])
        ]));
    }, [devicesByWorkflowForSite]);

    const selectedDeviceGuIds = useMemo(() => {
        return modalTargetType === 'SIGNIN' ? mergedSignInDeviceIds : mergedSignOutDeviceIds;
    }, [modalTargetType, mergedSignInDeviceIds, mergedSignOutDeviceIds]);

    return (
        <>
            <OpTable {...opTableProps} />
            {modalTargetType && devicesByWorkflowForSite && (
                <ActivityLogVideoModal
                    open={isVideoModalOpen}
                    onClose={() => setIsVideoModalOpen(false)}
                    targetType={modalTargetType}
                    videoDevices={videoDevices}
                    devicesByWorkflow={devicesByWorkflowForSite}
                    selectedDeviceGuIds={selectedDeviceGuIds}
                />
            )}
        </>

    );
}

export default VisitorsTable;